import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

// Built-in formatters for common use cases
const BUILT_IN_FORMATTERS = {
  cityState: (place) => {
    let city = '', state = '';
    place.addressComponents?.forEach(component => {
      const types = component.types;
      if (types.includes('locality') || types.includes('sublocality_level_1')) {
        city = city || component.longText;
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.shortText;
      }
    });
    return city && state ? `${city}, ${state}` : place.formattedAddress;
  },
  
  cityStateFull: (place) => {
    let city = '', state = '';
    place.addressComponents?.forEach(component => {
      const types = component.types;
      if (types.includes('locality') || types.includes('sublocality_level_1')) {
        city = city || component.longText;
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.longText;
      }
    });
    return city && state ? `${city}, ${state}` : place.formattedAddress;
  },
  
  fullAddress: (place) => place.formattedAddress,
  
  streetAddress: (place) => {
    let streetNumber = '', streetName = '';
    place.addressComponents?.forEach(component => {
      const types = component.types;
      if (types.includes('street_number')) {
        streetNumber = component.longText;
      }
      if (types.includes('route')) {
        streetName = component.longText;
      }
    });
    return streetNumber && streetName ? `${streetNumber} ${streetName}` : place.formattedAddress;
  },
  
  cityOnly: (place) => {
    let city = '';
    place.addressComponents?.forEach(component => {
      const types = component.types;
      if (types.includes('locality') || types.includes('sublocality_level_1')) {
        city = city || component.longText;
      }
    });
    return city || place.displayName || place.formattedAddress;
  }
};

// Hook for managing Google Places API loading with new API
const useGooglePlacesAPI = (apiKey) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsLoaded(true);
      return;
    }

    if (!apiKey) {
      setError('Google Places API key is required');
      return;
    }

    const loadGoogleMaps = async () => {
      try {
        // Check if script already exists
        const existingScript = document.querySelector(
          `script[src*="maps.googleapis.com/maps/api/js"]`
        );

        if (!existingScript) {
          // Create script with new loading pattern
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&v=beta`;
          script.async = true;
          script.defer = true;
          
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        // Wait for Google Maps to be available
        let attempts = 0;
        while (!window.google?.maps?.places && attempts < 50) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }

        if (window.google?.maps?.places) {
          setIsLoaded(true);
        } else {
          setError('Failed to load Google Places API');
        }
      } catch (err) {
        setError('Failed to load Google Places API: ' + err.message);
      }
    };

    loadGoogleMaps();
  }, [apiKey]);

  return { isLoaded, error };
};

// Modern Google Autocomplete Component using new Places API
const GoogleAutocomplete = ({
  apiKey,
  placeholder = 'Enter address...',
  value = '',
  onChange,
  onSelect,
  onError,
  formatter = 'cityState',
  customFormatter,
  types = ['geocode'],
  componentRestrictions = {},
  fields = ['addressComponents', 'formattedAddress', 'location', 'displayName'],
  debounceMs = 100,
  maxResults = 10,
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  itemClassName = '',
  highlightClassName = '',
  disabled = false,
  clearOnSelect = false,
  showLoadingState = true,
  icon,
  clearButton = true,
  ...inputProps
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  const { isLoaded, error: apiError } = useGooglePlacesAPI(apiKey);

  // Initialize session token when API is loaded
  useEffect(() => {
    if (isLoaded && window.google?.maps?.places?.AutocompleteSessionToken) {
      setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
    }
  }, [isLoaded]);

  // Get the formatter function
  const getFormatter = useMemo(() => {
    if (customFormatter && typeof customFormatter === 'function') {
      return customFormatter;
    }
    return BUILT_IN_FORMATTERS[formatter] || BUILT_IN_FORMATTERS.cityState;
  }, [formatter, customFormatter]);

  // Convert old API parameters to new API format
  const getRequestOptions = useMemo(() => {
    const options = {
      includedPrimaryTypes: types,
      sessionToken,
    };

    // if (window.google?.maps) {
    //   options.locationBias = new window.google.maps.Circle({
    //     center: new window.google.maps.LatLng(40.7128, -74.0060),
    //     radius: 50000.0
    //   });
    // }

    // Convert componentRestrictions to locationRestriction
    if (componentRestrictions?.country) {
      const countries = Array.isArray(componentRestrictions.country) 
        ? componentRestrictions.country 
        : [componentRestrictions.country];
      options.includedRegionCodes = countries.filter(c => typeof c === 'string' && c.length > 0);
    }
    return options;
  }, [types, componentRestrictions, sessionToken, window.google?.maps]);

  // Fetch predictions using new AutocompleteSuggestion API
  const fetchPredictions = useCallback(async (input) => {
    if (!input.trim() || !window.google?.maps?.places?.AutocompleteSuggestion) {
      setPredictions([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    try {
      const request = {
        ...getRequestOptions,
        input: input.trim(),
      };

      const { suggestions } = 
        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      
      if (suggestions && suggestions.length > 0) {
        // Filter only place predictions (not query suggestions)
        const placePredictions = suggestions
          .filter(suggestion => suggestion.placePrediction)
          .slice(0, maxResults);
        
        setPredictions(placePredictions);
        setIsOpen(true);
        setSelectedIndex(-1);
      } else {
        setPredictions([]);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Autocomplete error:', error);
      const errorMsg = `Prediction error: ${error.message}`;
      setError(errorMsg);
      onError?.(errorMsg);
      setPredictions([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, [getRequestOptions, maxResults, onError]);

  // Handle input change with debouncing
  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
    setError(null);
    setSelectedIndex(-1);

    // Clear existing debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the API call
    debounceRef.current = setTimeout(() => {
      fetchPredictions(newValue);
    }, debounceMs);
  }, [onChange, fetchPredictions, debounceMs]);

  // Handle place selection using new Place API
  const handlePlaceSelect = useCallback(async (suggestion, index) => {
    if (!suggestion?.placePrediction) return;

    setIsLoading(true);
    setIsOpen(false);
    setSelectedIndex(index);

    try {
      // Use the new Place API to get place details
      const place = suggestion.placePrediction.toPlace();
      
      const { place: placeResult } = await place.fetchFields({
        fields: fields.map(field => {
          // Map old field names to new ones if needed
          switch (field) {
            case 'address_components': return 'addressComponents';
            case 'formatted_address': return 'formattedAddress';
            case 'geometry': return 'location';
            case 'name': return 'displayName';
            default: return field;
          }
        })
      });

      if (placeResult) {
        const formattedValue = getFormatter(placeResult);
        
        if (!clearOnSelect) {
          setInputValue(formattedValue || '');
        } else {
          setInputValue('');
        }
        
        setError(null);
        
        // Create coordinates object compatible with old API
        const coordinates = placeResult.location ? {
          lat: placeResult.location.lat,
          lng: placeResult.location.lng
        } : null;
        
        onSelect?.({
          formatted: formattedValue,
          place: placeResult,
          prediction: suggestion.placePrediction,
          coordinates
        });

        // Refresh session token for next session
        if (window.google?.maps?.places?.AutocompleteSessionToken) {
          setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
        }
      }
    } catch (error) {
      console.error('Place details error:', error);
      const errorMsg = `Place details error: ${error.message}`;
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [fields, getFormatter, clearOnSelect, onSelect, onError]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < predictions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : predictions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handlePlaceSelect(predictions[selectedIndex], selectedIndex);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  }, [isOpen, predictions, selectedIndex, handlePlaceSelect]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear input
  const handleClear = useCallback(() => {
    setInputValue('');
    setPredictions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    onChange?.('');
    inputRef.current?.focus();
  }, [onChange]);

  // Sync external value prop (only when parent changes it, not on internal keystrokes)
  useEffect(() => {
    setInputValue(value);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Show error state
  if (apiError) {
    return (
      <div className={`relative ${className}`}>
        <input
          {...inputProps}
          className="w-full px-3 py-4 border border-red-300 rounded bg-red-50 text-red-700"
          value="API Error"
          disabled
        />
        <div className="text-sm text-red-600 mt-1">{apiError}</div>
      </div>
    );
  }

  // Show loading state while API loads
  if (!isLoaded) {
    return (
      <div className={`relative ${className}`}>
        <input
          {...inputProps}
          className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50"
          placeholder="Loading Google Maps..."
          disabled
        />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            {icon}
          </div>
        )}
        
        <input
          {...inputProps}
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (predictions.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={clsx(
            {
              'w-full px-3 py-4 border border-gray-300 rounded focus:outline-none ': !inputClassName,
              [inputClassName]: inputClassName
            },
            'focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            `${icon ? 'pl-10' : ''} ${clearButton && inputValue ? 'pr-10' : ''} `,
            `${error ? 'border-red-300 bg-red-50' : ''}`
          )}
          autoComplete="off"
        />
        
        {clearButton && inputValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
        
        {isLoading && showLoadingState && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {isOpen && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className={`absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto ${dropdownClassName}`}
        >
          {predictions.map((suggestion, index) => {
            const prediction = suggestion.placePrediction;
            return (
              <button
                key={prediction.placeId || index}
                type="button"
                onClick={() => handlePlaceSelect(suggestion, index)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                  selectedIndex === index ? `bg-blue-50 ${highlightClassName}` : ''
                } ${itemClassName}`}
              >
                <div className="font-medium text-gray-900">
                  {prediction.structuredFormat?.mainText?.text || prediction.text?.text}
                </div>
                {prediction.structuredFormat?.secondaryText?.text && (
                  <div className="text-sm text-gray-500 mt-1">
                    {prediction.structuredFormat.secondaryText.text}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
      
      {error && (
        <div className="text-sm text-red-600 mt-1">{error}</div>
      )}
    </div>
  );
};

GoogleAutocomplete.propTypes = {
  apiKey: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  onError: PropTypes.func,
  formatter: PropTypes.oneOf(['cityState', 'cityStateFull', 'fullAddress', 'streetAddress', 'cityOnly']),
  customFormatter: PropTypes.func,
  types: PropTypes.arrayOf(PropTypes.string),
  componentRestrictions: PropTypes.shape({
    country: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string)
    ])
  }),
  fields: PropTypes.arrayOf(PropTypes.string),
  debounceMs: PropTypes.number,
  maxResults: PropTypes.number,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  dropdownClassName: PropTypes.string,
  itemClassName: PropTypes.string,
  highlightClassName: PropTypes.string,
  disabled: PropTypes.bool,
  clearOnSelect: PropTypes.bool,
  showLoadingState: PropTypes.bool,
  icon: PropTypes.node,
  clearButton: PropTypes.bool
};

export default GoogleAutocomplete;